import { useEffect, useMemo, useRef, useState } from "react";
import {
  getFileByNumberAgent,
  PolicyTransactionDto,
} from "utils/api/policyTransactionApi";
import { StageType } from "utils/data/enum";

// Queue chip variants rendered by FileViewSection.
export type QueueVariant =
  | "history"
  | "close"
  | "qr"
  | "preclose"
  | "preclose-hold";

// One File View line item. Amounts are kept numeric here (null = "no value");
// FileViewSection formats them for display.
export type FileViewRow = {
  product: string;
  policyNumber: string;
  liabilityAmount: number | null;
  rateType: string;
  reissue: number | null;
  transCode: string;
  premiumFee: number | null;
  agentRetention: number | null;
  totalDue: number | null;
  queue: QueueVariant;
};

export type FileViewTotals = {
  liabilityAmount: number;
  premiumFee: number;
  agentRetention: number;
  totalDue: number;
};

const num = (v: number | null | undefined): number => v ?? 0;

// Stage code (+ on-hold flag) → queue chip. QUALITYREVIEW and anything
// unrecognized fall through to "qr".
function toQueue(stage: string | null, onHold: boolean): QueueVariant {
  switch (stage) {
    case StageType.HISTORY:
      return "history";
    case StageType.CLOSE:
      return "close";
    case StageType.PRECLOSE:
      return onHold ? "preclose-hold" : "preclose";
    default:
      return "qr";
  }
}

// Agent retention isn't returned by the server anymore (ActualRetention is a
// dropped legacy column), so derive it: the agent keeps premium minus the
// remittance owed to the underwriter, which is the Total Due.
function dtoToRow(dto: PolicyTransactionDto): FileViewRow {
  const premium = dto.ActualFee;
  const totalDue = dto.ActualRemittance;
  const hasMoney = premium != null || totalDue != null;
  return {
    product: dto.PolicyFormName ?? dto.ProductTypeAbbrTypeName ?? "",
    policyNumber: dto.PolicyNumber ?? "",
    liabilityAmount: dto.LiabilityAmount,
    rateType: dto.PricingRateType ?? "",
    reissue: dto.ReissueLiabilityAmount,
    transCode: dto.TransCode ?? "",
    premiumFee: premium,
    agentRetention: hasMoney ? num(premium) - num(totalDue) : null,
    totalDue,
    queue: toQueue(dto.StageTypeCode, dto.IsOnHold),
  };
}

function computeTotals(rows: FileViewRow[]): FileViewTotals {
  return rows.reduce<FileViewTotals>(
    (acc, r) => ({
      liabilityAmount: acc.liabilityAmount + num(r.liabilityAmount),
      premiumFee: acc.premiumFee + num(r.premiumFee),
      agentRetention: acc.agentRetention + num(r.agentRetention),
      totalDue: acc.totalDue + num(r.totalDue),
    }),
    { liabilityAmount: 0, premiumFee: 0, agentRetention: 0, totalDue: 0 }
  );
}

// Loads every line item belonging to the file (by file number + agent) that
// backs the File View section. Read-only.
export function useFileView(fileNumber?: string | null, agentID?: string | null) {
  const [rows, setRows] = useState<FileViewRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!fileNumber || !agentID) {
      setRows([]);
      return;
    }
    const myReq = ++reqIdRef.current;
    setIsLoading(true);
    getFileByNumberAgent(fileNumber, agentID)
      .then((dtos) => {
        if (reqIdRef.current !== myReq) return;
        setRows(dtos.map(dtoToRow));
        setIsLoading(false);
      })
      .catch(() => {
        if (reqIdRef.current === myReq) setIsLoading(false);
      });
  }, [fileNumber, agentID]);

  const totals = useMemo(() => computeTotals(rows), [rows]);

  return { rows, totals, isLoading };
}

export default useFileView;
